import * as React from 'react';
import { useApiTrigger } from '../../hooks/useApiTrigger';
import {
    PivotGrid,
    PivotGridContainer,
    PivotGridConfigurator,
    usePivotLocalDataService,
    PivotGridConfiguratorButton,
    sumAggregate,
    minAggregate,
    maxAggregate,
    averageAggregate,
} from '@progress/kendo-react-pivotgrid';

import { Typography } from '@mui/material';
import { Paper } from '@mui/material';
import { Box } from '@mui/material';
import { Loader } from '@progress/kendo-react-indicators';
import '../../../../styles/components/kendo/_pivotGrid.scss';

/* ------------------ CONFIG ------------------ */

// const pivotConfig = {
//     data: [
//         { Product: 'Chai', Category: 'Beverages', Price: 18, Region: 'Asia', Country: 'China' },
//         { Product: 'Chang', Category: 'Beverages', Price: 19, Region: 'Asia', Country: 'China' },
//         { Product: 'Ikura', Category: 'Seafood', Price: 31, Region: 'Asia', Country: 'Japan' },
//         { Product: 'Konbu', Category: 'Seafood', Price: 6, Region: 'Asia', Country: 'Japan' },
//         { Product: 'Chai', Category: 'Beverages', Price: 18, Region: 'Europe', Country: 'Germany' },
//         { Product: 'Chang', Category: 'Beverages', Price: 19, Region: 'Europe', Country: 'Germany' },
//         { Product: 'Ikura', Category: 'Seafood', Price: 31, Region: 'Europe', Country: 'Italy' },
//         { Product: 'Konbu', Category: 'Seafood', Price: 6, Region: 'Europe', Country: 'Italy' },
//         { Product: 'Chai', Category: 'Beverages', Price: 18, Region: 'North America', Country: 'United States' },
//         { Product: 'Chang', Category: 'Beverages', Price: 19, Region: 'North America', Country: 'United States' },
//         { Product: 'Ikura', Category: 'Seafood', Price: 31, Region: 'North America', Country: 'Canada' },
//         { Product: 'Konbu', Category: 'Seafood', Price: 6, Region: 'North America', Country: 'Canada' },
//         { Product: 'Chai', Category: 'Beverages', Price: 18, Region: 'South America', Country: 'Brazil' },
//         { Product: 'Chang', Category: 'Beverages', Price: 19, Region: 'South America', Country: 'Brazil' },
//         { Product: 'Ikura', Category: 'Seafood', Price: 31, Region: 'South America', Country: 'Argentina' },
//         { Product: 'Konbu', Category: 'Seafood', Price: 6, Region: 'South America', Country: 'Argentina' },
//     ],

//     dimensions: {
//         Category: { caption: 'Categories', field: 'Category' },
//         Product: { caption: 'Products', field: 'Product' },
//         Region: { caption: 'Regions', field: 'Region' },
//         Country: { caption: 'Countries', field: 'Country' },
//     },

//     measures: [
//         { name: 'Total', field: 'Price', aggregate: 'sum' },
//         { name: 'Max', field: 'Price', aggregate: 'max' },
//         { name: 'Min', field: 'Price', aggregate: 'min' },
//         { name: 'Average', field: 'Price', aggregate: 'average' },
//     ],
//     //OPTIONAL PARAMETERS
//     defaultRowAxes: [
//         { name: [ 'Region' ], expand: true },
//         { name: [ 'Country' ] },
//     ],

//     defaultColumnAxes: [
//         { name: [ 'Category' ], expand: true },
//         { name: [ 'Product' ] },
//     ],

//     defaultMeasureAxes: [ { name: [ 'Total' ] } ],

//     defaultSort: [
//         { field: 'Category', dir: 'asc' },
//         { field: 'Product', dir: 'asc' },
//         { field: 'Region', dir: 'asc' },
//         { field: 'Country', dir: 'asc' },
//     ],

//     defaultFilter: [],
// };

// /* ------------------ MAPPERS ------------------ */
const aggregateMap = {
    sum: sumAggregate,
    min: minAggregate,
    max: maxAggregate,
    average: averageAggregate,
    avg: averageAggregate,
};

const mapDimensions = (dimensions) => {
    const result = {};
    Object.entries(dimensions).forEach(([key, d]) => {
        result[key] = {
            caption: d.caption,
            displayValue: (item) => item[d.field],
            sortValue: (value) => value,
        };
    });
    return result;
};

const mapMeasures = (measures) =>
    measures.map((m) => {
        const aggregate = aggregateMap[m.aggregate];
        if (!aggregate) {
            throw new Error(`Aggregate no soportado: ${m.aggregate}`);
        }
        return { name: m.name, value: (item) => item[m.field], aggregate };
    });


const GridPivot = React.forwardRef(({ data, filters, returnData }, ref) => {
    const { dto } = useApiTrigger(data, filters);
    const [showConfigurator, setShowConfigurator] = React.useState(false);
    const serviceArgs = React.useMemo(() => {
        const dimensions = mapDimensions(data.config.dimensions);
        return {
            data: dto,
            dimensions,
            measures: mapMeasures(data.config.measures),
            defaultRowAxes: data.config.defaultRowAxes || [],
            defaultColumnAxes: data.config.defaultColumnAxes || [],
            defaultMeasureAxes: data.config.defaultMeasureAxes || [],
            defaultSort: Object.keys(dimensions).map(k => ({ field: k, dir: 'asc' })),
            defaultFilter: data.config?.defaultFilter || [],
        };
    }, [dto, data.config]);
    // const serviceArgs = React.useMemo(() => {
    //     const dimensions = mapDimensions(pivotConfig.dimensions);
    //     return {
    //         data: pivotConfig.data,
    //         dimensions,
    //         measures: mapMeasures(pivotConfig.measures),
    //         defaultRowAxes: pivotConfig.defaultRowAxes || [],
    //         defaultColumnAxes: pivotConfig.defaultColumnAxes || [],
    //         defaultMeasureAxes: pivotConfig.defaultMeasureAxes || [],
    //         defaultSort: Object.keys(dimensions).map(k => ({field: k, dir: 'asc'})),
    //         defaultFilter: pivotConfig?.defaultFilter || [],
    //     };
    // }, [pivotConfig]);
    const getData = () => returnData(dto, data.accion || 'Reporte_Grafica');
    React.useImperativeHandle(ref, () => ({ exportChartData: getData }));

    const { pivotProps, configuratorProps, state } = usePivotLocalDataService(serviceArgs);
    const handleButtonClick = React.useCallback(() => {
        setShowConfigurator(!showConfigurator);
    }, [showConfigurator]);

    return (
        <Paper>
            <Box
                display="flex"
                alignItems="center"
                justifyContent="space-between"
                px={2}
                py={1}
                borderBottom="1px solid"
                borderColor="divider"
            >
                <Typography variant="h6" fontWeight={600}>
                    {data.accion || 'Reporte_Grafica'}
                </Typography>
            </Box>
            <Box position="relative">
                <PivotGridContainer>
                    <PivotGrid {...pivotProps} style={{ height: state.loading ? 350 : undefined, maxHeight: state.loading ? undefined : 700 }} />
                    {showConfigurator && <PivotGridConfigurator {...configuratorProps} />}
                    <PivotGridConfiguratorButton onClick={handleButtonClick} />
                    {state.loading && <Loader style={{
                        position: 'absolute',
                        top: '60%',
                        left: '50%',
                        transform: 'translate(-50%,-50%)'
                    }}
                        size="large" />
                    }
                </PivotGridContainer>
            </Box>
        </Paper>
    );
});
export default GridPivot;